import { SelectOptionData } from '@arco-design/web-vue/es/select/interface';

import {
  generateColumnsKey,
  commaSplitFunc,
  trimFunc,
  tranMaterialPropertyFunc,
  isNonNegativeNumber,
  getCurrencyPriceVOS,
  excelDateToTimestamp,
  semicolonSplitFunc,
} from '@/utils/excel';
import currencyArr from '@/constants/json/currency.json';
import { checkAuthority } from '@/utils/common';
import {
  excessHandleList,
  excessTypeList,
  reportModeList,
  settingImportanceList,
} from './product';

export interface PlanType {
  name: string;
  value: number;
}
// 商品企划类型
export const PLAN_TYPE: Array<SelectOptionData> = [
  {
    name: '首单',
    value: 1,
  },
  {
    name: '客户补单',
    value: 2,
  },
  {
    name: '翻单',
    value: 3,
  },
  {
    name: '齐色',
    value: 4,
  },
  {
    name: '线上订单',
    value: 5,
  },
];

export const FINISH_IN_STATE = [
  {
    text: '默认',
    val: 0,
  },
  {
    text: '待入库',
    val: 1,
    color: 'orangeRed',
  },
  {
    text: '部分入库',
    val: 2,
    color: 'orange',
  },
  {
    text: '放弃入库',
    val: 4,
    color: 'red',
  },
  {
    text: '已撤销',
    val: 9,
    color: 'gray',
  },
];
export const FINISHED_IN_STATE = [
  { val: 1, text: '草稿' },
  { val: 2, text: '已入库', color: 'orange' },
  { val: 9, text: '已撤销', color: 'gray' },
];

export const FINISH_OUT_STATE = [
  {
    text: '默认',
    val: 0,
  },
  {
    text: '待出库',
    val: 1,
    color: 'orangeRed',
  },
  {
    text: '部分出库',
    val: 2,
    color: 'orange',
  },
  {
    text: '放弃出库',
    val: 4,
    color: 'red',
  },
  {
    text: '已撤销',
    val: 9,
    color: 'gray',
  },
];
export const FINISHED_OUT_STATE = [
  { val: 1, text: '草稿' },
  { val: 2, text: '已出库', color: 'orange' },
  { val: 9, text: '已撤销', color: 'gray' },
];

export const sampleSourceMap = {
  1: '自研',
  2: '买手',
  3: '外供',
  4: '改款',
  自研: 1,
  买手: 2,
  外供: 3,
  改款: 4,
} as const;

export const roundWayMap = {
  1: '向上取整10',
  2: '向上取整100',
  3: '向上取整1000',
  向上取整10: 1,
  向上取整100: 2,
  向上取整1000: 3,
};
export const fabricTypeMap = {
  针织: 1,
  梭织: 2,
  default: 0,
};
export const SEX_STATUS = {
  GENERAL: 0,
  MEN: 1,
  WOMEN: 2,
  CHILDREN: 3,
};
export const sexMap: Recordable = {
  [SEX_STATUS.GENERAL]: '通款',
  [SEX_STATUS.MEN]: '男款',
  [SEX_STATUS.WOMEN]: '女款',
  [SEX_STATUS.CHILDREN]: '童装',
  通款: SEX_STATUS.GENERAL,
  男款: SEX_STATUS.MEN,
  女款: SEX_STATUS.WOMEN,
  童装: SEX_STATUS.CHILDREN,
};
export const onSaleMap: Recordable = {
  1: '启用',
  0: '停用',
};
export const coreMap: Recordable = {
  1: '无',
  2: '裁床',
  3: '生产完成',
  4: '车间生产',
  5: '尾部',
  6: '纸样',
  无: 1,
  裁床: 2,
  生产完成: 3,
  车间生产: 4,
  尾部: 5,
  纸样: 6,
};

export const sexOptions = [
  {
    text: '通款',
    val: SEX_STATUS.GENERAL,
  },
  {
    text: '男款',
    val: SEX_STATUS.MEN,
  },
  {
    text: '女款',
    val: SEX_STATUS.WOMEN,
  },
  {
    text: '童装',
    val: SEX_STATUS.CHILDREN,
  },
];
const isOpenFunc = (
  { cellValue }: any,
  { openText = '开启', closeText = '关闭' } = {}
) => {
  return cellValue ? openText : closeText;
};
const goodsColumns = [
  {
    value: [
      { val: 'imageUrlList', text: '商品图片', formatter: commaSplitFunc },
    ],
  },
  {
    value: [{ val: 'name', text: '商品名称(必填)', formatter: trimFunc }],
  },
  {
    value: [{ val: 'goodsSn', text: '商品货号(必填)', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'barCode', text: '商品条码(不可重复)', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'designSn', text: '设计款号', formatter: trimFunc }],
  },
  {
    value: [{ val: 'supplierSn', text: '供应商货号', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'colorNames', text: '颜色(必填)', formatter: commaSplitFunc },
    ],
  },
  {
    value: [
      { val: 'sizeNames', text: '尺码(必填)', formatter: commaSplitFunc },
    ],
  },
  {
    value: [
      {
        val: 'sampleSource',
        text: '来源',
        formatter: (val: any) => {
          const sampleSource = String(
            val || ''
          ).trim() as keyof typeof sampleSourceMap;

          return sampleSourceMap[sampleSource] || 0;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'devTime',
        text: '开发日期',
        formatter: (val: any) => {
          if (!val) return undefined;
          return excelDateToTimestamp(val);
        },
      },
    ],
  },
  {
    value: [{ val: 'clientName', text: '客户', formatter: trimFunc }],
  },
  {
    value: [{ val: 'customDesignSn', text: '客户款号', formatter: trimFunc }],
  },
  {
    value: [{ val: 'categoryName', text: '分类', formatter: trimFunc }],
  },
  {
    value: [{ val: 'blandName', text: '品牌', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'year',
        text: '年份',
        formatter: (val: any) => {
          let year = new Date().getFullYear();

          const yearSplit = String(val || `${year}上`).split('年');
          if (yearSplit) {
            year = parseInt(yearSplit[0], 10);
          }
          if (
            Number.isInteger(year) &&
            yearSplit &&
            yearSplit.length > 1 &&
            yearSplit[1].indexOf('下') !== -1
          ) {
            year += 0.5;
          }
          return year;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'month',
        text: '月份',
        formatter: (val: any) => {
          if (!val) return '';
          const monthSplit = String(val).split('月');
          let month;
          if (monthSplit) {
            month = parseInt(monthSplit[0], 10);
          }
          if (Number.isInteger(month)) {
            return month;
          }
          return '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'sex',
        text: '性别',
        formatter: (val: any) => {
          return sexMap[String(val || '').trim()] ?? '';
        },
      },
    ],
  },
  {
    value: [{ val: 'seasonName', text: '季节', formatter: trimFunc }],
  },
  {
    value: [{ val: 'gramWeight', text: '克重(g/㎡)', formatter: trimFunc }],
  },
  {
    value: [{ val: 'composition', text: '成分', formatter: trimFunc }],
  },
  {
    value: [{ val: 'rangesName', text: '波段', formatter: trimFunc }],
  },
  {
    value: [{ val: 'designerName', text: '设计师', formatter: trimFunc }],
  },
  {
    value: [{ val: 'patternMakerName', text: '打版师', formatter: trimFunc }],
  },
  {
    value: [{ val: 'factoryName', text: '加工厂', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'designAssistantName', text: '设计助理', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'followerName', text: '跟单员', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'executeStandardName', text: '执行标准', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'securityTypeName', text: '安全类别', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'isOnSale',
        text: '启用状态',
        formatter: (val: any) => {
          if (String(val || '').indexOf('停用') !== -1) {
            return 0;
          }
          return 1;
        },
      },
    ],
  },
  {
    value: [{ val: 'remark', text: '备注(20字内)', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'tagPrice',
        text: '吊牌价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'retailPriceFactory',
        text: '建议零售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'tradePrice',
        text: '批发价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'costPrice',
        text: '成本价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'retailPriceArea',
        text: '零售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'storehouseStoreLocations',
        text: '库位',
        formatter: (val: any) => {
          const replace1Str: string = String(val || '').replace(/;/g, '；');
          const wordSplit1 = replace1Str.split('；');
          const res: any[] = [];
          wordSplit1.forEach((str: string) => {
            const replace2Str: string = String(str || '').replace(/:/g, '：');
            const wordSplit2: string[] = replace2Str.split('：');
            const storehouseName = wordSplit2[0];
            const locationStr: string = wordSplit2[1];
            const locationList = commaSplitFunc(locationStr);
            if (Array.isArray(locationList)) {
              const obj = {
                storehouseName,
                storeLocations: locationList.map((lcStr: any) => ({
                  storageLocationName: lcStr,
                })),
              };
              res.push(obj);
            }
          });
          return res;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'currencyName',
        text: '货币名',
        formatter: (val: any) => {
          const { currencyName } = getCurrencyPriceVOS(val);
          return currencyName;
        },
      },
      {
        val: 'currencyCode',
        text: '货币代码',
        formatter: (val: any) => {
          const { currencyCode } = getCurrencyPriceVOS(val);
          return currencyCode;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'currencyPrice',
        text: '外币售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
];
export const materialsCols = [
  {
    value: [
      {
        val: 'materialImageUrlList',
        text: '物料图片',
        formatter: commaSplitFunc,
      },
    ],
  },
  {
    value: [{ val: 'goodsSn', text: '商品货号', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'goodsColorName', text: '商品颜色(必填)', formatter: trimFunc },
    ],
  },
  {
    value: [
      { val: 'materialName', text: '物料名称(必填)', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'materialSn', text: '物料货号(必填)', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'materialColorName', text: '物料颜色(必填)', formatter: trimFunc },
    ],
  },
  {
    value: [
      {
        val: 'materialBarcode',
        text: '条码(不可重复)',
        formatter: trimFunc,
      },
    ],
  },
  {
    value: [
      {
        val: 'materialProperties',
        text: '物料规格',
        formatter: tranMaterialPropertyFunc,
      },
    ],
  },
  {
    value: [
      {
        val: 'number',
        text: '单件平均用量(必填)',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'materialTypeName',
        text: '物料类型(必填)',
        formatter: trimFunc,
      },
    ],
  },
  {
    value: [
      {
        val: 'materialFabricType',
        text: '面料属性',
        formatter: (val: string) => {
          return fabricTypeMap[val as keyof typeof fabricTypeMap] || 0;
        },
      },
    ],
  },
  {
    value: [{ val: 'materialCategoryName', text: '分类', formatter: trimFunc }],
  },
  {
    value: [{ val: 'purchaseUnitName', text: '采购单位', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'purchasePrice',
        text: '采购单价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'convertRate',
        text: '转换系数',
        formatter: trimFunc,
      },
    ],
  },
  {
    value: [{ val: 'materialUnitName', text: '基础单位', formatter: trimFunc }],
  },
  {
    value: [{ val: 'supplierName', text: '供应商', formatter: trimFunc }],
  },
  {
    value: [{ val: 'supplierSn', text: '供应商货号', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'materialComposition', text: '面料成分', formatter: trimFunc },
    ],
  },
  {
    value: [
      { val: 'materialPostProcess', text: '后处理', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'materialPart', text: '使用部位', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialLossRate', text: '损耗率', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialFabricWidth', text: '幅宽', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialGramWeight', text: '克重', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialQunce', text: '安数', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'materialMeterOutput', text: '出米数', formatter: trimFunc },
    ],
  },
  {
    value: [{ val: 'materialRollWeight', text: '卷重', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialPaperBucket', text: '纸筒', formatter: trimFunc }],
  },
  {
    value: [{ val: 'materialEmptyGap', text: '空差', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'materialEmptyGapRate', text: '空差比', formatter: trimFunc },
    ],
  },
  {
    value: [
      {
        val: 'materialMultiplyingPower',
        text: '领料建议倍率',
        formatter: trimFunc,
      },
    ],
  },
  {
    value: [
      {
        val: 'materialRoundWay',
        text: '领料取整方式',
        formatter: (val: string) => {
          return roundWayMap[val as keyof typeof roundWayMap] || 0;
        },
      },
    ],
  },
  {
    value: [{ val: 'materialRemark', text: '备注', formatter: trimFunc }],
  },
];
export const materialsColumns = generateColumnsKey(materialsCols);

export const noAvgNumberMaterialsColumns = generateColumnsKey(
  materialsColumns.filter(
    (col: Recordable) =>
      !col.value.some((item: Recordable) => item.val === 'number')
  )
);
const workProcessesColumns = [
  {
    value: [{ val: 'goodsSn', text: '商品货号', formatter: trimFunc }],
  },
  {
    value: [
      { val: 'workProcessName', text: '工序名称(必填)', formatter: trimFunc },
    ],
  },
  {
    value: [
      {
        val: 'price',
        text: '单价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'reportType',
        text: '汇报方式(必填)',
        formatter: (val: any) => {
          // 默认为
          let type;
          if (String(val || '').indexOf('汇报整体') !== -1) {
            // 汇报整体数量
            type = 1;
          }
          if (String(val || '').indexOf('分色码') !== -1) {
            // 分色码汇报数量
            type = 2;
          }
          if (String(val || '').indexOf('整体完成') !== -1) {
            type = 3;
          }
          return type;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'excessType',
        text: '超量汇报类型',
        formatter: (val: any) => {
          // 默认为裁床数
          let res = 'cutBedNumber';
          if (String(val || '').indexOf('订单数') !== -1) {
            res = 'produceNumber';
          }
          if (String(val || '').indexOf('上一节点数') !== -1) {
            res = 'lastWorkProcessNumber';
          }
          return res;
        },
      },
      {
        val: 'excessLimit',
        text: '超量百分比限制',
        formatter: (val: any) => {
          // 当超过裁床数10%时仅提示
          const regResult = val.match(/\b(\d+)%/);
          if (regResult && regResult.length > 1) {
            return regResult[1];
          }
          return undefined;
        },
      },
      {
        val: 'excessHandle',
        text: '超量处理方式',
        formatter: (val: any) => {
          // 默认为仅提示
          let res = 'toast';
          if (String(val || '').indexOf('禁止提交') !== -1) {
            res = 'ban';
          }
          return res;
        },
      },
    ],
  },
  {
    value: [
      {
        val: 'core',
        text: '设置为关键',
        formatter: (val: any) => {
          // 默认为无 1
          return coreMap[String(val || '').trim()] || 1;
        },
      },
    ],
  },
  {
    value: [{ val: 'leaderNames', text: '负责人', formatter: commaSplitFunc }],
  },
];
const partsColumns = [
  {
    value: [{ val: 'imageUrlList', text: '图片', formatter: commaSplitFunc }],
  },
  {
    value: [{ val: 'goodsSn', text: '商品货号', formatter: trimFunc }],
  },
  {
    value: [{ val: 'name', text: '部位(必填)', formatter: trimFunc }],
  },
  {
    value: [{ val: 'measureMethod', text: '测量方法', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'errorRange',
        text: '误差范围',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  // {
  //   value: [
  //     {
  //       val: 'gradingDifference',
  //       text: '档差',
  //       formatter: (val: any) => {
  //         return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
  //       },
  //     },
  //   ],
  // },
];
const otherTechniquesColumns = [
  {
    value: [{ val: 'goodsSn', text: '商品货号' }],
  },
  {
    value: [{ val: 'name', text: '工艺名称(必填)' }],
  },
  {
    value: [{ val: 'description', text: '工艺描述' }],
  },
];
const skuColumns = [
  {
    value: [{ val: 'goodsSn', text: '商品货号' }],
  },
  {
    value: [{ val: 'colorName', text: '商品颜色(必填)', formatter: trimFunc }],
  },
  {
    value: [{ val: 'sizeName', text: '商品尺码(必填)', formatter: trimFunc }],
  },
  {
    value: [
      {
        val: 'skuBarCode',
        text: '条码(不可重复)',
        formatter: trimFunc,
      },
    ],
  },
  {
    value: [
      {
        val: 'skuCode',
        text: '单品编码(不可重复，对接第三方时使用)',
        formatter: trimFunc,
      },
    ],
  },
];
// 商品导入修改 基本信息
export const exportGoodsInfoColumns = [
  {
    title: '商品名称',
    field: 'name',
    width: 135,
    align: 'left',
    value: [{ val: 'name', text: '商品名称(必填)', formatter: trimFunc }],
  },
  {
    title: '商品货号',
    field: 'goodsSn',
    width: 100,
    align: 'left',
    value: [{ val: 'goodsSn', text: '商品货号(必填)', formatter: trimFunc }],
  },
  {
    title: '商品条码（不可重复）',
    field: 'barCode',
    width: 200,
    value: [
      { val: 'barCode', text: '商品条码(不可重复)', formatter: trimFunc },
    ],
  },
  {
    title: '设计款号（不可重复）',
    field: 'designSn',
    width: 200,
    value: [{ val: 'designSn', text: '设计款号', formatter: trimFunc }],
  },
  {
    title: '供应商货号',
    field: 'supplierSn',
    width: 130,
    value: [{ val: 'supplierSn', text: '供应商货号', formatter: trimFunc }],
  },
  {
    title: '颜色（必填，使用逗号隔开）',
    field: 'colorNames',
    align: 'left',
    width: 200,
    value: [
      { val: 'colorNames', text: '颜色(必填)', formatter: commaSplitFunc },
    ],
  },
  {
    title: '尺码（必填，使用逗号隔开）',
    field: 'sizeNames',
    align: 'left',
    width: 200,
    value: [
      { val: 'sizeNames', text: '尺码(必填)', formatter: commaSplitFunc },
    ],
  },
  {
    title: '来源',
    field: 'sampleSource',
    width: 80,
    formatter: ({ cellValue }: any): string => {
      const sampleSource = String(
        cellValue || ''
      ).trim() as keyof typeof sampleSourceMap;

      return `${sampleSourceMap[sampleSource] || ''}`;
    },
    dataValidation: {
      formulae: [`"${Object.keys(sampleSourceMap).slice(4).join(',')}"`],
    },
    value: [
      {
        val: 'sampleSource',
        text: '来源',
        formatter: (val: any) => {
          const sampleSource = String(
            val || ''
          ).trim() as keyof typeof sampleSourceMap;

          return sampleSourceMap[sampleSource] || 0;
        },
      },
    ],
  },
  {
    title: '开发日期',
    field: 'devTime',
    width: 100,
    type: 'day',
    format: 'YYYY-MM-DD',
    default: '',
    value: [
      {
        val: 'devTime',
        text: '开发日期',
        formatter: (val: any) => {
          if (!val) return undefined;
          if (typeof val === 'number') {
            return excelDateToTimestamp(val);
          }
          return new Date(val).getTime();
        },
      },
    ],
  },
  {
    title: '客户',
    field: 'clientName',
    width: 80,
    value: [{ val: 'clientName', text: '客户', formatter: trimFunc }],
  },
  {
    title: '客户款号',
    field: 'customDesignSn',
    width: 80,
    value: [{ val: 'customDesignSn', text: '客户款号', formatter: trimFunc }],
  },
  {
    title: '分类',
    field: 'categoryName',
    width: 80,
    align: 'left',
    value: [{ val: 'categoryName', text: '分类', formatter: trimFunc }],
  },
  {
    title: '品牌',
    field: 'blandName',
    width: 80,
    value: [{ val: 'blandName', text: '品牌', formatter: trimFunc }],
  },
  {
    title: '年份',
    field: 'year',
    width: 90,
    formatter: ({ row }: any): string => {
      if (row.year) {
        if (row.year.includes('.5')) {
          return `${Math.floor(row.year)}年下`;
        }
        return `${Math.floor(row.year)}年上`;
      }
      return '';
    },
    value: [
      {
        val: 'year',
        text: '年份',
        formatter: (val: any) => {
          let year = new Date().getFullYear();

          const yearSplit = String(val || '').split('年');
          if (yearSplit) {
            year = parseInt(yearSplit[0], 10);
          }
          if (
            Number.isInteger(year) &&
            yearSplit &&
            yearSplit.length > 1 &&
            yearSplit[1].indexOf('下') !== -1
          ) {
            year += 0.5;
          }
          return year;
        },
      },
    ],
  },
  {
    title: '性别',
    field: 'sex',
    width: 80,
    formatter: ({ row }: any): string => {
      return sexMap[row.sex] || '';
    },
    dataValidation: {
      formulae: [`"${Object.values(sexMap).slice(0, 4).join(',')}"`],
    },
    value: [
      {
        val: 'sex',
        text: '性别',
        formatter: (val: any) => {
          return sexMap[String(val || '').trim()] ?? 0;
        },
      },
    ],
  },
  {
    title: '季节',
    field: 'seasonName',
    width: 80,
    value: [{ val: 'seasonName', text: '季节', formatter: trimFunc }],
  },
  {
    title: '克重(g/㎡)',
    field: 'gramWeight',
    width: 80,
    align: 'right',
    value: [{ val: 'gramWeight', text: '克重(g/㎡)', formatter: trimFunc }],
  },
  {
    title: '成分',
    field: 'composition',
    value: [{ val: 'composition', text: '成分', formatter: trimFunc }],
  },
  {
    title: '波段',
    field: 'rangesName',
    width: 80,
    value: [{ val: 'rangesName', text: '波段', formatter: trimFunc }],
  },
  {
    title: '设计师',
    field: 'designerName',
    width: 80,
    value: [{ val: 'designerName', text: '设计师', formatter: trimFunc }],
  },
  {
    title: '打版师',
    field: 'patternMakerName',
    width: 80,
    align: 'left',
    value: [{ val: 'patternMakerName', text: '打版师', formatter: trimFunc }],
  },
  {
    title: '加工厂',
    field: 'factoryName',
    width: 90,
    value: [{ val: 'factoryName', text: '加工厂', formatter: trimFunc }],
  },
  {
    title: '设计助理',
    field: 'designAssistantName',
    width: 80,
    value: [
      { val: 'designAssistantName', text: '设计助理', formatter: trimFunc },
    ],
  },
  {
    title: '跟单员',
    field: 'followerName',
    width: 80,
    value: [{ val: 'followerName', text: '跟单员', formatter: trimFunc }],
  },
  {
    title: '执行标准',
    field: 'executeStandardName',
    width: 135,
    align: 'left',
    value: [
      { val: 'executeStandardName', text: '执行标准', formatter: trimFunc },
    ],
  },
  {
    title: '安全类别',
    field: 'securityTypeName',
    width: 135,
    align: 'left',
    value: [{ val: 'securityTypeName', text: '安全类别', formatter: trimFunc }],
  },
  {
    title: '启用状态',
    field: 'isOnSale',
    width: 80,
    render: 'Class',
    formatter: (row: any) =>
      isOpenFunc(row, { openText: '启用', closeText: '停用' }),
    dataValidation: {
      allowBlank: false,
      formulae: [`"启用,停用"`],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: '必填项缺失',
      error: '请输入有效值',
    },
    value: [
      {
        val: 'isOnSale',
        text: '启用状态',
        formatter: (val: any) => {
          if (String(val || '').indexOf('停用') !== -1) {
            return 0;
          }
          return 1;
        },
      },
    ],
  },
  {
    title: '备注',
    field: 'remark',
    width: 135,
    value: [{ val: 'remark', text: '备注', formatter: trimFunc }],
  },
  {
    title: '吊牌价',
    width: 80,
    field: 'tagPrice',
    headerAlign: 'right',
    align: 'right',
    hiddenFun: () => !checkAuthority('goodsTagPriceGet'),
    value: [
      {
        val: 'tagPrice',
        text: '吊牌价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '建议零售价',
    width: 80,
    field: 'retailPriceFactory',
    headerAlign: 'right',
    align: 'right',
    hiddenFun: () => !checkAuthority('goodsRetailPriceGet'),
    value: [
      {
        val: 'retailPriceFactory',
        text: '建议零售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '批发价',
    width: 80,
    field: 'tradePrice',
    headerAlign: 'right',
    align: 'right',
    hiddenFun: () =>
      !checkAuthority(['goodsTradePriceGet', 'goodsTradePriceEdit']),
    value: [
      {
        val: 'tradePrice',
        text: '批发价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '成本价',
    width: 100,
    field: 'costPrice',
    headerAlign: 'right',
    align: 'right',
    hiddenFun: () => !checkAuthority('goodsCostPriceGet'),
    value: [
      {
        val: 'costPrice',
        text: '成本价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '零售价',
    width: 80,
    field: 'retailPriceArea',
    headerAlign: 'right',
    align: 'right',
    hiddenFun: () => !checkAuthority('goodsRetailPriceAreaGet'),
    value: [
      {
        val: 'retailPriceArea',
        text: '零售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '库位',
    field: 'storehouseLocationsText',
    align: 'left',
    width: 200,
    value: [
      {
        val: 'storehouseStoreLocations',
        text: '库位',
        formatter: (val: any) => {
          const replace1Str: string = String(val || '').replace(/;/g, '；');
          const wordSplit1 = replace1Str.split('；');
          const res: any[] = [];
          wordSplit1.forEach((str: string) => {
            const replace2Str: string = String(str || '').replace(/:/g, '：');
            const wordSplit2: string[] = replace2Str.split('：');
            const storehouseName = wordSplit2[0];
            const locationStr: string = wordSplit2[1];
            const locationList = commaSplitFunc(locationStr);
            if (Array.isArray(locationList)) {
              const obj = {
                storehouseName,
                storeLocations: locationList.map((lcStr: any) => ({
                  storageLocationName: lcStr,
                })),
              };
              res.push(obj);
            }
          });
          return res;
        },
      },
    ],
  },
  {
    title: '外币',
    width: 80,
    field: 'currency',
    headerAlign: 'right',
    align: 'right',
    formatter: ({ row }: any): string => {
      const cellValue = row.goodsCurrencyPriceVOS;
      if (!Array.isArray(cellValue)) return '';
      return (
        cellValue
          .map((item: any) => `${item.currencyName}(${item.currencyCode})`)
          .join('、') || ''
      );
    },
    dataValidation: {
      formulae: [`外币!$A$2:$A$${currencyArr.length + 1}`],
    },
    hiddenFun: () => !checkAuthority('currencyPrice'),
    value: [
      {
        val: 'currencyName',
        text: '货币名',
        formatter: (val: any) => {
          const { currencyName } = getCurrencyPriceVOS(val);
          return currencyName;
        },
      },
      {
        val: 'currencyCode',
        text: '货币代码',
        formatter: (val: any) => {
          const { currencyCode } = getCurrencyPriceVOS(val);
          return currencyCode;
        },
      },
    ],
  },
  {
    title: '外币售价',
    width: 80,
    field: 'currencyPrice',
    headerAlign: 'right',
    align: 'right',
    formatter: ({ row }: any): string => {
      const cellValue = row.goodsCurrencyPriceVOS;
      if (!Array.isArray(cellValue)) return '';
      return (
        cellValue.map((item: any) => `${item.currencyPrice}`).join('、') || ''
      );
    },
    hiddenFun: () => !checkAuthority('currencyPrice'),
    value: [
      {
        val: 'currencyPrice',
        text: '外币售价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
];
// 商品导入修改 生产节点
export const exportWorkProcessesColumns = [
  {
    title: '商品货号',
    field: 'goodsSn',
    width: 100,
    align: 'left',
    value: [{ val: 'goodsSn', text: '商品货号', formatter: trimFunc }],
  },
  {
    title: '工序名称（必填）',
    field: 'workProcessName',
    width: 130,
    value: [
      { val: 'workProcessName', text: '工序名称(必填)', formatter: trimFunc },
    ],
  },
  {
    title: '单价',
    field: 'price',
    width: 80,
    value: [
      {
        val: 'price',
        text: '单价',
        formatter: (val: any) => {
          return !Number.isNaN(parseFloat(val)) ? parseFloat(val) : '';
        },
      },
    ],
  },
  {
    title: '汇报方式（必填）',
    field: 'reportType',
    width: 130,
    dataValidation: {
      allowBlank: false,
      formulae: [`"${reportModeList.map((item) => item.text).join(',')}"`],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: '必填项缺失',
      error: '请输入有效值',
    },
    formatter: ({ cellValue }: any): string => {
      return (
        reportModeList.find((item: any) => item.value === cellValue)?.text || ''
      );
    },
    value: [
      {
        val: 'reportType',
        text: '汇报方式(必填)',
        formatter: (val: any) => {
          return (
            reportModeList.find(
              (item: any) => item.text === String(val || '').trim()
            )?.value || null
          );
        },
      },
    ],
  },
  {
    title: '超量汇报',
    field: 'excessLimit',
    align: 'left',
    width: 200,
    formatter: ({ row }: any): string => {
      const excessType =
        excessTypeList.find((item: any) => item.value === row.excessType)
          ?.text || '';
      const excessHandle =
        excessHandleList.find((item: any) => item.value === row.excessHandle)
          ?.text || '';
      let excessText = excessType;
      if (row.excessType === 'appointWorkProcess') {
        excessText = `${excessType}（${row.excessWorkProcessName ?? ''}）`;
      }
      return `当超过${excessText}${row.excessLimit ?? ''}%时${excessHandle}`;
    },
    value: [
      {
        val: 'excessType',
        text: '超量汇报类型',
        formatter: (val: any) => {
          // 默认为裁床数
          return (
            excessTypeList.find((item) => {
              return String(val || '').indexOf(item.text) !== -1;
            })?.value ?? 'cutBedNumber'
          );
        },
      },
      {
        val: 'excessWorkProcessName',
        text: '指定节点名称',
        formatter: (val: any) => {
          const match = String(val || '').match(/其他节点[（(]([^)]+)[)）]/);
          return match ? match[1]?.trim() ?? '' : undefined;
        },
      },
      {
        val: 'excessLimit',
        text: '超量百分比限制',
        formatter: (val: any) => {
          // 当超过裁床数10%时仅提示
          const regResult = val.match(/\b(\d+)%/);
          if (regResult && regResult.length > 1) {
            return regResult[1];
          }
          return undefined;
        },
      },
      {
        val: 'excessHandle',
        text: '超量处理方式',
        formatter: (val: any) => {
          // 默认为仅提示
          let res = 'toast';
          if (String(val || '').indexOf('禁止提交') !== -1) {
            res = 'ban';
          }
          return res;
        },
      },
    ],
  },
  {
    title: '用时（天）',
    field: 'workHours',
    align: 'left',
    width: 100,
    value: [
      {
        val: 'workHours',
        text: '用时（天）',
        formatter: (val: any) => {
          if (Number.isInteger(val) || val === 0 || val === 0.5) {
            return val;
          }
          return Math.round(val * 2) / 2; // 回滚到最近的0.5
        },
      },
    ],
  },
  {
    title: '多久开始（天）',
    field: 'startAfterDays',
    width: 130,
    value: [
      {
        val: 'startAfterDays',
        text: '多久开始（天）',
        formatter: (val: any) => {
          if (Number.isInteger(val) || val === 0 || val === 0.5) {
            return val;
          }
          return Math.round(val * 2) / 2; // 回滚到最近的0.5
        },
      },
    ],
  },
  {
    title: '设置为关键节点',
    field: 'core',
    width: 130,
    dataValidation: {
      allowBlank: false,
      formulae: [
        `"${settingImportanceList.map((item) => item.text).join(',')}"`,
      ],
      showErrorMessage: true,
      errorStyle: 'error',
      errorTitle: '必填项缺失',
      error: '请输入有效值',
    },
    formatter: ({ cellValue }: any) => {
      return (
        settingImportanceList.find(
          (item: any) => item.value === Number(cellValue)
        )?.text || '无'
      );
    },
    value: [
      {
        val: 'core',
        text: '设置为关键节点',
        formatter: (val: any) => {
          // 默认为无 1
          return (
            settingImportanceList.find(
              (item: any) => item.text === String(val || '').trim()
            )?.value || 1
          );
        },
      },
    ],
  },
  {
    title: '显示统计',
    field: 'isShowStatistics',
    width: 80,
    dataValidation: {
      formulae: [`"开启,关闭"`],
    },
    formatter: isOpenFunc,
    value: [
      {
        val: 'isShowStatistics',
        text: '显示统计',
        formatter: (val: any) => {
          if (String(val || '').indexOf('开启') !== -1) {
            return 1;
          }
          return 0;
        },
      },
    ],
  },
  {
    title: '工序外发',
    field: 'isOutward',
    width: 80,
    dataValidation: {
      formulae: [`"开启,关闭"`],
    },
    formatter: isOpenFunc,
    value: [
      {
        val: 'isOutward',
        text: '工序外发',
        formatter: (val: any) => {
          if (String(val || '').indexOf('开启') !== -1) {
            return 1;
          }
          return 0;
        },
      },
    ],
  },
  {
    title: '原料外发',
    field: 'isOutwardMaterial',
    children: [
      {
        pField: 'isOutwardMaterial',
        field: 'isOutwardMaterial',
        title: '状态',
        width: 80,
        dataValidation: {
          formulae: [`"开启,关闭"`],
        },
        formatter: isOpenFunc,
        value: [
          {
            val: 'isOutwardMaterial',
            text: '状态',
            formatter: (val: any) => {
              if (String(val || '').indexOf('开启') !== -1) {
                return 1;
              }
              return 0;
            },
          },
        ],
      },
      {
        pField: 'isOutwardMaterial',
        field: 'materialUnitName',
        title: '原料单位',
        width: 80,
        value: [
          { val: 'materialUnitName', text: '原料单位', formatter: trimFunc },
        ],
      },
      {
        pField: 'isOutwardMaterial',
        field: 'isRecoverMaterial',
        title: '回收原料',
        width: 80,
        dataValidation: {
          formulae: [`"开启,关闭"`],
        },
        formatter: isOpenFunc,
        value: [
          {
            val: 'isRecoverMaterial',
            text: '回收原料',
            formatter: (val: any) => {
              if (String(val || '').indexOf('开启') !== -1) {
                return 1;
              }
              return 0;
            },
          },
        ],
      },
      {
        pField: 'isOutwardMaterial',
        field: 'isOutwardGoods',
        title: '记录外发件数',
        width: 100,
        dataValidation: {
          formulae: [`"开启,关闭"`],
        },
        formatter: isOpenFunc,
        value: [
          {
            val: 'isOutwardGoods',
            text: '记录外发件数',
            formatter: (val: any) => {
              if (String(val || '').indexOf('开启') !== -1) {
                return 1;
              }
              return 0;
            },
          },
        ],
      },
    ],
  },
  {
    title: '扫菲节点',
    field: 'workTicketState',
    width: 80,
    dataValidation: {
      formulae: [`"开启,关闭"`],
    },
    formatter: isOpenFunc,
    value: [
      {
        val: 'workTicketState',
        text: '扫菲节点',
        formatter: (val: any) => {
          if (String(val || '').indexOf('开启') !== -1) {
            return 1;
          }
          return 0;
        },
      },
    ],
  },
  {
    title: '负责人',
    field: 'leaderNames',
    width: 80,
    formatter: ({ row }: any) => {
      return row.leaders?.map((item: any) => item.leaderName).join('；') || '';
    },
    value: [
      { val: 'leaderNames', text: '负责人', formatter: semicolonSplitFunc },
    ],
  },
  {
    title: '完成时通知',
    field: 'isFinishNotify',
    children: [
      {
        pField: 'isFinishNotify',
        field: 'nextNodeLeader',
        title: '下一节点负责人',
        width: 110,
        dataValidation: {
          formulae: [`"开启,关闭"`],
        },
        formatter: ({ row }: any) => {
          const cellValue = row.notifier?.some((item: any) => item.type === 4);
          return isOpenFunc({ cellValue });
        },
        value: [
          {
            val: 'nextNodeLeader',
            text: '下一节点负责人',
            formatter: (val: any) => {
              if (String(val || '').indexOf('开启') !== -1) {
                return 1;
              }
              return 0;
            },
          },
        ],
      },
      {
        pField: 'isFinishNotify',
        field: 'follower',
        title: '生产单跟单员',
        width: 100,
        dataValidation: {
          formulae: [`"开启,关闭"`],
        },
        formatter: ({ row }: any) => {
          const cellValue = row.notifier?.some((item: any) => item.type === 3);
          return isOpenFunc({ cellValue });
        },
        value: [
          {
            val: 'follower',
            text: '生产单跟单员',
            formatter: (val: any) => {
              if (String(val || '').indexOf('开启') !== -1) {
                return 1;
              }
              return 0;
            },
          },
        ],
      },
      {
        pField: 'isFinishNotify',
        field: 'staffIdList',
        title: '指定员工',
        width: 100,
        formatter: ({ row }: any) => {
          const staffIdList: Recordable[] = [];
          row.notifier?.forEach((item: any) => {
            if (item.type === 1) {
              staffIdList.push(item.leaderName);
            }
          });
          return staffIdList.join('；');
        },
        value: [
          {
            val: 'staffIdList',
            text: '指定员工',
            formatter: (val: any) => {
              // 使用英文逗号分割
              const wordSplit1 = String(val || '').split(';');
              const wordSplit2: Recordable[] = [];
              wordSplit1.forEach((item) => {
                // 再使用中文逗号分割
                const words = item.split('；');
                words.forEach((word: any) => {
                  if (word) {
                    wordSplit2.push({
                      type: 1,
                      leaderName: word.trim(),
                    });
                  }
                });
              });
              if (wordSplit2.length > 0) {
                return wordSplit2;
              }
              return undefined;
            },
          },
        ],
      },
    ],
  },
];
export const templateColumns: any = {
  goodsColumns: generateColumnsKey(goodsColumns),
  materialsColumns,
  workProcessesColumns: generateColumnsKey(workProcessesColumns),
  partsColumns: generateColumnsKey(partsColumns),
  otherTechniquesColumns: generateColumnsKey(otherTechniquesColumns),
  skuColumns: generateColumnsKey(skuColumns),
};

export const staticMaterialFields: string[] = [
  ...materialsColumns.map((col: Recordable) => col?.value?.[0]?.val),
  'rowIdxKey',
];
export const staticPartFields: string[] = [
  'name',
  'measureMethod',
  'errorRange',
  // 'gradingDifference',
  'goodsSn',
  'rowIdxKey',
  'imageUrlList',
];

export const previewImportColumns = [
  {
    title: '格式状态',
    field: 'checkStatusText',
    align: 'left',
    type: 'slot',
    fixed: 'left',
  },
  {
    title: '异常说明',
    field: 'errorText',
    align: 'left',
    className: 'color-red',
    fixed: 'left',
    minWidth: 200,
    showTooltip: true,
    line: 2,
  },
  {
    title: '行号',
    field: 'rowIdxKey',
    align: 'left',
  },
  {
    title: '商品图片',
    field: 'imageUrlList',
    type: 'images',
    width: 300,
  },
  {
    title: '商品名称（必填）',
    field: 'name',
    align: 'left',
  },
  {
    title: '商品货号（必填）',
    field: 'goodsSn',
    fixed: 'left',
    width: 150,
  },
  {
    title: '商品条码（不可重复）',
    field: 'barCode',
    align: 'left',
  },
  {
    title: '设计款号',
    field: 'designSn',
    align: 'left',
  },
  {
    title: '供应商货号',
    field: 'supplierSn',
    align: 'left',
  },
  {
    title: '颜色（必填，使用逗号隔开）',
    field: 'colorNames',
    width: 200,
    align: 'left',
    formatter: ({ row }: any): string => {
      if (Array.isArray(row.colorNames)) {
        return row.colorNames.join('、');
      }
      return row.colorNames;
    },
  },
  {
    title: '尺码（必填，使用逗号隔开）',
    field: 'sizeNames',
    width: 200,
    align: 'left',
    formatter: ({ row }: any): string => {
      if (Array.isArray(row.sizeNames)) {
        return row.sizeNames.join('、');
      }
      return row.sizeNames;
    },
  },
  {
    title: '来源',
    field: 'sampleSource',
    align: 'left',
    formatter: ({ row, cellValue }: any): string => {
      const sampleSource = String(
        cellValue || ''
      ).trim() as keyof typeof sampleSourceMap;

      return `${sampleSourceMap[sampleSource] || ''} `;
    },
  },
  {
    title: '开发日期',
    field: 'devTime',
    align: 'left',
    formatter: ['dateFormatter', 'YYYY-MM-DD'],
  },
  {
    title: '客户',
    field: 'clientName',
    align: 'left',
  },
  {
    title: '客户款号',
    field: 'customDesignSn',
    align: 'left',
  },
  {
    title: '分类',
    field: 'categoryName',
    align: 'left',
  },
  {
    title: '品牌',
    field: 'blandName',
    align: 'left',
  },
  {
    title: '年份',
    field: 'year',
    align: 'left',
    formatter: ({ row }: any): string => {
      if (!row.year) return '';
      const arr = String(row.year || '').split('.');
      const _ = arr.length === 1 ? (arr[1] = '上半年') : (arr[1] = '下半年');
      return arr.join('');
    },
  },
  {
    title: '月份',
    field: 'month',
    width: 100,
    default: '',
    formatter: ({ cellValue }: { cellValue: number }): string => {
      return cellValue ? `${cellValue}月` : '';
    },
  },
  {
    title: '性别',
    field: 'sex',
    align: 'left',
    formatter: ({ cellValue }: any): string => {
      return `${sexMap[cellValue] || ''} `;
    },
  },
  {
    title: '季节',
    field: 'seasonName',
    align: 'left',
  },
  {
    title: '克重（g/㎡）',
    field: 'gramWeight',
    align: 'left',
  },
  {
    title: '成分',
    field: 'composition',
    align: 'left',
  },
  {
    title: '波段',
    field: 'rangesName',
    align: 'left',
  },
  {
    title: '设计师',
    field: 'designerName',
    align: 'left',
  },
  {
    title: '打版师',
    field: 'patternMakerName',
    align: 'left',
  },
  {
    hiddenFun: () => !checkAuthority('supplierInfoFactoryGetDataPermission'),
    title: '加工厂',
    field: 'factoryName',
    align: 'left',
  },
  {
    title: '设计助理',
    field: 'designAssistantName',
    align: 'left',
  },
  {
    title: '跟单员',
    field: 'followerName',
    align: 'left',
  },
  {
    title: '执行标准',
    field: 'executeStandardName',
    align: 'left',
  },
  {
    title: '安全类别',
    field: 'securityTypeName',
    align: 'left',
  },
  {
    title: '启用状态',
    field: 'isOnSale',
    align: 'left',
    formatter: ({ cellValue }: any): string => {
      return `${onSaleMap[cellValue] || ''} `;
    },
  },
  {
    title: '备注',
    field: 'remark',
    align: 'left',
  },
  {
    title: '吊牌价',
    field: 'tagPrice',
    align: 'right',
  },
  {
    title: '建议零售价',
    field: 'retailPriceFactory',
    align: 'right',
  },
  {
    title: '批发价',
    field: 'tradePrice',
    align: 'right',
  },
  {
    title: '成本价',
    field: 'costPrice',
    align: 'right',
  },
  {
    title: '零售价',
    field: 'retailPriceArea',
    align: 'right',
  },
  {
    title: '外币售价',
    field: 'goodsCurrencyPriceVOS',
    align: 'right',
    formatter: ({ row }: any): string => {
      return row.goodsCurrencyPriceVOS
        ?.map((item: any) => `${item.currencyCode} ${item.currencyPrice}`)
        ?.join(',');
    },
  },
  {
    title: '库位',
    field: 'storehouseLocation',
    align: 'right',
  },
];

export const excelSheetsArr = [
  {
    colKey: 'goodsColumns',
    key: 'goodss',
    templateRow: 1,
    name: '基本信息表',
    validateFields: [
      { field: 'name', message: '请填写商品名称' },
      { field: 'goodsSn', message: '请填写商品货号' },
      { field: 'colorNames', message: '请填写商品颜色', type: 'Array' },
      { field: 'sizeNames', message: '请填写商品尺码', type: 'Array' },
    ],
  },
  {
    colKey: 'materialsColumns',
    key: 'materials',
    templateRow: 1,
    name: '物料清单表',
    validateFields: [
      { field: 'goodsColorName', message: '请填写商品颜色' },
      { field: 'materialName', message: '请填写物料名称' },
      { field: 'materialSn', message: '请填写物料货号' },
      { field: 'materialColorName', message: '请填写物料颜色' },
      { field: 'materialTypeName', message: '请选择物料类型' },
    ],
    validateNumberFields: [
      { field: 'materialFabricWidth', message: '幅宽只能填写数值类型的非负数' },
      { field: 'materialGramWeight', message: '克重只能填写数值类型的非负数' },
      { field: 'materialLossRate', message: '损耗率只能填写数值类型的非负数' },
      {
        field: 'materialMultiplyingPower',
        message: '领料建议倍率只能填写数值类型不超过1000的非负整数',
        validFunc: (val: unknown) =>
          isNonNegativeNumber(val) || Number(val) > 1000,
      },
    ],
  },
  {
    colKey: 'workProcessesColumns',
    key: 'workProcesses',
    templateRow: 1,
    name: '生产节点表',
    validateFields: [
      { field: 'workProcessName', message: '请填写工序名称' },
      { field: 'reportType', message: '请填写汇报方式' },
    ],
  },
  {
    colKey: 'partsColumns',
    key: 'parts',
    templateRow: 2,
    name: '尺寸指示表',
    validateFields: [{ field: 'name', message: '请填写部位名称' }],
  },
  {
    colKey: 'otherTechniquesColumns',
    key: 'techniques',
    templateRow: 1,
    name: '其他工艺表',
    validateFields: [{ field: 'name', message: '请填写工艺名称' }],
  },
  {
    colKey: 'skuColumns',
    key: 'goodsSkuInfos',
    templateRow: 1,
    name: '单品条码表',
    validateFields: [
      { field: 'colorName', message: '请填写商品颜色' },
      { field: 'sizeName', message: '请填写商品尺码' },
    ],
  },
];

export const excelSheetsOnModifyModeArr = [
  {
    colKey: 'goodsColumns',
    key: 'goodss',
    name: '基本信息',
    columnsType: 'exportGoodsInfoColumns',
    validateFields: [
      { field: 'name', message: '请填写商品名称' },
      { field: 'goodsSn', message: '请填写商品货号' },
      { field: 'colorNames', message: '请填写商品颜色', type: 'Array' },
      { field: 'sizeNames', message: '请填写商品尺码', type: 'Array' },
    ],
  },
  {
    colKey: 'workProcessesColumns',
    key: 'workProcesses',
    name: '生产节点',
    columnsType: 'exportWorkProcessesColumns',
    validateFields: [
      { field: 'workProcessName', message: '请填写工序名称' },
      { field: 'reportType', message: '请填写汇报方式' },
    ],
    hiddenFun: () =>
      !checkAuthority('goodsDataWorkProcessGet') ||
      !checkAuthority('goodsDataWorkProcessEdit'),
  },
  {
    colKey: 'currencyColumns',
    key: 'currencyColumns',
    name: '外币',
    columnsType: 'exportCurrencyColumns',
    hiddenFun: () => true,
  },
];
